import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  DEFAULT_ACTIVE_SHOP_LOCALES,
  DEFAULT_SHOP_LOCALE,
  languageToLocale,
  localeToLanguage,
  normalizeActiveShopLocales,
  normalizeShopLocale,
  readLocaleFromDocumentCookie,
  resolveShopLocale,
  stripLocalePrefix,
  withLocalePrefix,
  writeLocaleCookie,
  type ShopLanguage,
  type ShopLocale,
} from '../shopLocale'

const resolveBrowserAcceptLanguage = () => {
  if (typeof navigator === 'undefined') return undefined
  if (Array.isArray(navigator.languages) && navigator.languages.length) {
    return navigator.languages.join(',')
  }
  return navigator.language
}

type UseShopLocaleStateInput = {
  initialLocale?: ShopLocale
  profileLocale?: string | null
  availableLocales?: ShopLocale[]
}

export const useShopLocaleState = ({ initialLocale, profileLocale, availableLocales }: UseShopLocaleStateInput) => {
  const resolvedAvailableLocales = useMemo(
    () => normalizeActiveShopLocales(availableLocales ?? DEFAULT_ACTIVE_SHOP_LOCALES),
    [availableLocales],
  )

  const [locale, setLocaleState] = useState<ShopLocale>(() => {
    const normalizedInitial = normalizeShopLocale(initialLocale, {
      availableLocales: resolvedAvailableLocales,
    })
    return normalizedInitial ?? resolvedAvailableLocales[0] ?? DEFAULT_SHOP_LOCALE
  })

  useEffect(() => {
    if (typeof window === 'undefined') return

    const syncLocale = () => {
      const pathname = window.location.pathname
      const localeOptions = { availableLocales: resolvedAvailableLocales }
      const { hadLocalePrefix, pathnameWithoutLocale } = stripLocalePrefix(pathname, localeOptions)
      const cookieLocale = readLocaleFromDocumentCookie(localeOptions)
      const resolved = resolveShopLocale({
        pathname,
        cookieLocale,
        profileLocale: profileLocale ?? null,
        acceptLanguage: resolveBrowserAcceptLanguage(),
        fallbackLocale: initialLocale ?? resolvedAvailableLocales[0] ?? DEFAULT_SHOP_LOCALE,
        availableLocales: resolvedAvailableLocales,
      })

      setLocaleState((current) => (current === resolved ? current : resolved))
      writeLocaleCookie(resolved)

      if (!hadLocalePrefix) {
        const search = window.location.search || ''
        const hash = window.location.hash || ''
        const target = withLocalePrefix(`${pathnameWithoutLocale}${search}${hash}`, resolved, localeOptions)
        const current = `${window.location.pathname}${search}${hash}`
        if (target !== current) {
          window.history.replaceState({}, '', target)
        }
      }
    }

    syncLocale()
    window.addEventListener('popstate', syncLocale)
    return () => window.removeEventListener('popstate', syncLocale)
  }, [initialLocale, profileLocale, resolvedAvailableLocales])

  const setLocale = useCallback((nextLocale: ShopLocale) => {
    if (typeof window === 'undefined') return

    const localeOptions = { availableLocales: resolvedAvailableLocales }
    const resolvedLocale = normalizeShopLocale(nextLocale, localeOptions)
    if (!resolvedLocale) return

    setLocaleState(resolvedLocale)
    writeLocaleCookie(resolvedLocale)

    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`
    const target = withLocalePrefix(current, resolvedLocale, localeOptions)
    if (target !== current) {
      window.history.replaceState({}, '', target)
      window.dispatchEvent(new PopStateEvent('popstate'))
    }
  }, [resolvedAvailableLocales])

  const setLanguage = useCallback((nextLanguage: ShopLanguage) => {
    const nextLocale = languageToLocale(nextLanguage, resolvedAvailableLocales)
    setLocale(nextLocale)
  }, [resolvedAvailableLocales, setLocale])

  return {
    locale,
    availableLocales: resolvedAvailableLocales,
    language: localeToLanguage(locale),
    setLocale,
    setLanguage,
  }
}
